import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
	Backdrop,
	Button,
	Fade,
	IconButton,
	Modal,
	Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import { usePwaInstall } from "utils/usePwaInstall";

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal,
		backgroundColor: "rgba(28, 43, 74, 0.45)",
	},
	sheet: {
		position: "fixed",
		left: 0,
		right: 0,
		bottom: 0,
		outline: "none",
		backgroundColor: theme.palette.white,
		borderTopLeftRadius: 20,
		borderTopRightRadius: 20,
		padding: theme.spacing(3, 3, 4),
		boxShadow: "0 -8px 32px rgba(28, 43, 74, 0.18)",
		maxWidth: 480,
		margin: "0 auto",
		zIndex: theme.zIndex.modal + 1,
	},
	closeButton: {
		position: "absolute",
		top: theme.spacing(1),
		right: theme.spacing(1),
		color: theme.palette.text.tertiary,
	},
	iconWrap: {
		width: 72,
		height: 72,
		borderRadius: "50%",
		margin: "0 auto",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.palette.background.secondary,
		color: theme.palette.brand,
		marginBottom: theme.spacing(2),
	},
	title: {
		fontWeight: 700,
		color: theme.palette.brand,
		marginBottom: theme.spacing(1),
	},
	body: {
		color: theme.palette.text.tertiary,
		marginBottom: theme.spacing(3),
		lineHeight: 1.5,
	},
	steps: {
		textAlign: "left",
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(3),
		paddingLeft: theme.spacing(2),
		"& li": {
			marginBottom: theme.spacing(1),
		},
	},
	installButton: {
		backgroundColor: theme.palette.brand,
		color: theme.palette.white,
		textTransform: "none",
		fontWeight: 600,
		fontSize: 16,
		borderRadius: 12,
		padding: theme.spacing(1.5, 2),
		boxShadow: "none",
		"&:hover": {
			backgroundColor: "#152238",
			boxShadow: "none",
		},
	},
}));

const PwaInstallPrompt = ({ delayMs }) => {
	const classes = useStyles();
	const {
		open,
		isIos,
		showIosHelp,
		install,
		dismiss,
	} = usePwaInstall({ delayMs });

	if (!open) {
		return null;
	}

	return (
		<Modal
			open={open}
			onClose={dismiss}
			closeAfterTransition
			BackdropComponent={Backdrop}
			BackdropProps={{
				timeout: 300,
				className: classes.backdrop,
			}}
			aria-labelledby="pwa-install-title"
			aria-describedby="pwa-install-description"
		>
			<Fade in={open}>
				<div className={classes.sheet} role="dialog">
					<IconButton
						className={classes.closeButton}
						onClick={dismiss}
						aria-label="Close install prompt"
						size="small"
					>
						<CloseIcon />
					</IconButton>

					<div className={classes.iconWrap} aria-hidden="true">
						<PhoneIphoneIcon fontSize="large" />
					</div>

					<Typography
						id="pwa-install-title"
						variant="h5"
						align="center"
						className={classes.title}
					>
						Save as Mobile App
					</Typography>

					{showIosHelp ? (
						<>
							<Typography
								id="pwa-install-description"
								variant="body1"
								align="center"
								className={classes.body}
							>
								Add ForTheRecord to your Home Screen for fast
								access and offline use.
							</Typography>
							<ol className={classes.steps}>
								<li>Tap the Share button in Safari</li>
								<li>Scroll and tap “Add to Home Screen”</li>
								<li>Tap Add to confirm</li>
							</ol>
							<Button
								fullWidth
								variant="contained"
								className={classes.installButton}
								onClick={dismiss}
							>
								Got it
							</Button>
						</>
					) : (
						<>
							<Typography
								id="pwa-install-description"
								variant="body1"
								align="center"
								className={classes.body}
							>
								Install the ForTheRecord app on your device for
								fast access, push alerts, and a direct home
								screen launcher.
							</Typography>
							<Button
								fullWidth
								variant="contained"
								className={classes.installButton}
								onClick={install}
								startIcon={!isIos ? <GetAppIcon /> : undefined}
							>
								Install Now
							</Button>
						</>
					)}
				</div>
			</Fade>
		</Modal>
	);
};

PwaInstallPrompt.propTypes = {
	delayMs: PropTypes.number,
};

PwaInstallPrompt.defaultProps = {
	delayMs: 2500,
};

export default PwaInstallPrompt;
